import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  opcionesAPI,
  type Sucursal,
  type Deposito,
  sucursalDepositoIds,
  depositoSirveSucursal,
} from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as {
    message?: unknown;
    details?: unknown;
    data?: Record<string, unknown>;
  };
  if (typeof e.message === "string" && e.message.trim() && e.message !== "Bad Request") {
    return e.message;
  }
  const details = e.details ?? e.data;
  if (typeof details === "string" && details.trim()) return details;
  if (details && typeof details === "object") {
    const d = details as Record<string, unknown>;
    if (typeof d.detail === "string" && d.detail.trim()) return d.detail;
    for (const key of ["nombre", "depositos", "non_field_errors", "detail"]) {
      const v = d[key];
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
    for (const v of Object.values(d)) {
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

function sortIdStrings(ids: string[]): string[] {
  return [...new Set(ids)].sort((a, b) => Number(a) - Number(b));
}

function depositosIdsForSucursal(sucursal: Sucursal, allDepositos: Deposito[]): string[] {
  if (Array.isArray(sucursal.depositos)) {
    return sortIdStrings(sucursalDepositoIds(sucursal).map(String));
  }
  const sucId = Number(sucursal.id);
  if (!sucId) return [];
  return sortIdStrings(
    allDepositos
      .filter((d) => d.id != null && depositoSirveSucursal(d, sucId))
      .map((d) => String(d.id)),
  );
}

interface FormData {
  codigo: string;
  nombre: string;
  direccion: string;
  ciudad: string;
  provincia: string;
  telefono: string;
  email: string;
  depositos: string[];
  esCasaCentral: boolean;
}

const emptyForm: FormData = {
  codigo: "",
  nombre: "",
  direccion: "",
  ciudad: "",
  provincia: "",
  telefono: "",
  email: "",
  depositos: [],
  esCasaCentral: false,
};

interface SucursalesABMProps {
  selectedItem?: Sucursal | null;
  onBack?: () => void;
  onSaved?: () => void;
}

export function SucursalesABM({ selectedItem, onBack, onSaved }: SucursalesABMProps) {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [originalFormData, setOriginalFormData] = useState<FormData>(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSucursales, setFilteredSucursales] = useState<Sucursal[]>([]);
  const [allSucursales, setAllSucursales] = useState<Sucursal[]>([]);
  const [allDepositos, setAllDepositos] = useState<Deposito[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sucs, deps] = await Promise.all([
          opcionesAPI.listSucursales(),
          opcionesAPI.listDepositos(),
        ]);
        setAllSucursales(sucs);
        setAllDepositos(deps);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Error al cargar datos");
      }
    };
    loadData();
  }, []);

  const selectSucursal = (sucursal: Sucursal) => {
    const loadedData: FormData = {
      codigo: sucursal.codigo,
      nombre: sucursal.nombre,
      direccion: sucursal.direccion || "",
      ciudad: sucursal.ciudad || "",
      provincia: sucursal.provincia || "",
      telefono: sucursal.telefono || "",
      email: sucursal.email || "",
      depositos: depositosIdsForSucursal(sucursal, allDepositos),
      esCasaCentral: Boolean(sucursal.esCasaCentral ?? sucursal.es_casa_central),
    };
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setHasChanges(false);
    setShowSuggestions(false);
    setIsEditing(true);
  };

  useEffect(() => {
    if (selectedItem) {
      selectSucursal(selectedItem);
    } else {
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
    }
    // selectSucursal lee allDepositos solo como fallback si la sucursal no trae depositos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  const handleNombreChange = (value: string) => {
    setFormData({ ...formData, nombre: value });
    checkForChanges({ ...formData, nombre: value });

    if (value.trim()) {
      const filtered = allSucursales.filter(s =>
        s.nombre?.toLowerCase().includes(value.toLowerCase()) ||
        s.codigo?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSucursales(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleNombreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (filteredSucursales.length > 0) {
        selectSucursal(filteredSucursales[0]);
      } else if (formData.nombre.trim() && !isEditing) {
        setHasChanges(true);
        setShowSuggestions(false);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const checkForChanges = (data: FormData) => {
    const changed = JSON.stringify(data) !== JSON.stringify(originalFormData);
    setHasChanges(changed);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    checkForChanges(newData);
  };

  const toggleDeposito = (depositoId: string, checked: boolean) => {
    const next = checked
      ? [...formData.depositos, depositoId]
      : formData.depositos.filter((id) => id !== depositoId);
    const newData = { ...formData, depositos: sortIdStrings(next) };
    setFormData(newData);
    checkForChanges(newData);
  };

  const handleCasaCentralChange = (checked: boolean) => {
    const newData = { ...formData, esCasaCentral: checked };
    setFormData(newData);
    checkForChanges(newData);
  };

  const goToListado = () => {
    setShowDeleteDialog(false);
    if (onBack) onBack();
    else if (onSaved) onSaved();
  };

  const handleSave = async () => {
    const nombreNorm = formData.nombre.trim();
    if (!nombreNorm) {
      toast.error("El nombre es requerido");
      return;
    }

    const dup = allSucursales.find(
      (s) =>
        s.nombre?.trim().toLowerCase() === nombreNorm.toLowerCase() &&
        (!isEditing || s.codigo !== formData.codigo),
    );
    if (dup) {
      toast.error(
        "Ya existe una sucursal con ese nombre (sin distinguir mayúsculas/minúsculas).",
      );
      return;
    }

    setLoading(true);
    try {
      const depositoIds = formData.depositos
        .map((id) => parseInt(id, 10))
        .filter((n) => Number.isFinite(n));
      const otherChanged =
        formData.nombre.trim() !== originalFormData.nombre.trim() ||
        (formData.direccion || "") !== (originalFormData.direccion || "") ||
        (formData.ciudad || "") !== (originalFormData.ciudad || "") ||
        (formData.provincia || "") !== (originalFormData.provincia || "") ||
        (formData.telefono || "") !== (originalFormData.telefono || "") ||
        (formData.email || "") !== (originalFormData.email || "");

      if (isEditing && formData.codigo) {
        const payload: Partial<Sucursal> = {
          depositos: depositoIds,
          esCasaCentral: formData.esCasaCentral,
        };
        if (otherChanged) {
          payload.nombre = nombreNorm;
          payload.direccion = formData.direccion;
          payload.ciudad = formData.ciudad;
          payload.provincia = formData.provincia;
          payload.telefono = formData.telefono;
          payload.email = formData.email;
        }
        await opcionesAPI.updateSucursal(formData.codigo, payload);
        toast.success("Sucursal actualizada");
      } else {
        await opcionesAPI.createSucursal({
          codigo: "",
          nombre: nombreNorm,
          direccion: formData.direccion,
          ciudad: formData.ciudad,
          provincia: formData.provincia,
          telefono: formData.telefono,
          email: formData.email,
          activo: true,
          esCasaCentral: formData.esCasaCentral,
          depositos: depositoIds,
        });
        toast.success("Sucursal creada");
      }

      dataRefresh.emit("depositos");
      goToListado();
    } catch (error: unknown) {
      console.error("Error saving sucursal:", error);
      toast.error(apiErrorMessage(error, "Error al guardar sucursal"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await opcionesAPI.deleteSucursal(formData.codigo);
      toast.success("Sucursal eliminada");
      dataRefresh.emit("depositos");
      goToListado();
    } catch (error: unknown) {
      console.error("Error deleting sucursal:", error);
      toast.error(apiErrorMessage(error, "Error al eliminar sucursal"));
      setShowDeleteDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setOriginalFormData(emptyForm);
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleNew}>
          Nueva Sucursal
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleNombreChange(e.target.value)}
            onKeyDown={handleNombreKeyDown}
            onFocus={() => {
              if (formData.nombre.trim() && filteredSucursales.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Escriba para buscar o crear..."
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {showSuggestions && filteredSucursales.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredSucursales.map((sucursal) => (
                <button
                  key={sucursal.codigo}
                  type="button"
                  onClick={() => selectSucursal(sucursal)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex justify-between items-center"
                >
                  <span>{sucursal.nombre}</span>
                  <span className="text-xs text-muted-foreground">{sucursal.codigo}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Depósitos</label>
          <div className="border border-input rounded-lg px-3 py-2 max-h-40 overflow-y-auto space-y-2 bg-input">
            {allDepositos.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay depósitos cargados</p>
            ) : (
              allDepositos.map((dep) => {
                const did = String(dep.id || dep.codigo);
                return (
                  <label
                    key={dep.codigo}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.depositos.includes(did)}
                      onCheckedChange={(checked) =>
                        toggleDeposito(did, checked === true)
                      }
                    />
                    <span>{dep.nombre}</span>
                  </label>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Una sucursal puede despachar de varios depósitos. Puede crearla sin depósitos y asociarlos después.
          </p>
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={formData.esCasaCentral}
              onCheckedChange={(checked) => handleCasaCentralChange(checked === true)}
              disabled={isEditing && originalFormData.esCasaCentral && formData.esCasaCentral}
            />
            <span>Casa central</span>
            {formData.esCasaCentral ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                Casa central
              </span>
            ) : null}
          </label>
          <p className="text-xs text-muted-foreground mt-1">
            Exactamente una sucursal. Marcar otra reasigna el flag. No se puede desmarcar ni borrar sin reasignar.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Dirección</label>
          <input
            type="text"
            value={formData.direccion}
            onChange={(e) => handleInputChange("direccion", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Ciudad</label>
          <input
            type="text"
            value={formData.ciudad}
            onChange={(e) => handleInputChange("ciudad", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Provincia</label>
          <input
            type="text"
            value={formData.provincia}
            onChange={(e) => handleInputChange("provincia", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Teléfono</label>
          <input
            type="text"
            value={formData.telefono}
            onChange={(e) => handleInputChange("telefono", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
      </div>
      <FormFooter
        onBack={onBack}
        onDelete={handleDelete}
        deleteDisabled={!formData.codigo || loading}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saving={loading}
      />
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar esta sucursal?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
